/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { NavigateOptions, URLSearchParamsInit } from 'react-router';

import { saveConversationMode } from '@/sections/insights/aiAssistant/config/storage';
import { extractConversationId } from '@/sections/insights/aiAssistant/utils/conversationId';
import {
  type FailedMessage,
  toFailedMessage,
} from '@/sections/insights/aiAssistant/utils/failedMessage';

interface UseSummonOptions {
  summonMessage: string | null;
  summonMode: 'query' | 'agent' | null;
  isAIDisabledButBypassed: boolean;
  /**
   * Pending-chat write-through: sets the mode for the about-to-be-created
   * conversation. Does NOT persist (no id yet); useSummon persists against the
   * freshly-created newId via saveConversationMode below.
   */
  setPendingChatMode: (mode: 'query' | 'agent') => void;
  createConversation: (
    message: string,
    mode: 'query' | 'agent'
  ) => Promise<any>;
  setSearchParams: (
    nextInit:
      | URLSearchParamsInit
      | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions
  ) => void;
  setFailedMessage: (msg: FailedMessage | null) => void;
}

export function useSummon({
  summonMessage,
  summonMode,
  isAIDisabledButBypassed,
  setPendingChatMode,
  createConversation,
  setSearchParams,
  setFailedMessage,
}: UseSummonOptions): void {
  const summonProcessedRef = useRef(false);

  // Handle summon parameters from URL (Ask Marcus AI)
  useEffect(() => {
    if (
      !summonMessage ||
      !summonMode ||
      summonProcessedRef.current ||
      isAIDisabledButBypassed
    )
      return;
    summonProcessedRef.current = true;

    // Clear summon params from URL immediately to prevent re-triggering
    setSearchParams(
      prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('summonMessage');
        newParams.delete('summonMode');
        return newParams;
      },
      { replace: true }
    );

    // Validate summonMode against allowlist to prevent injection
    const validatedMode: 'query' | 'agent' =
      summonMode === 'query' || summonMode === 'agent' ? summonMode : 'query';

    // Enforce max length on summonMessage to prevent prompt injection via oversized input
    const MAX_SUMMON_MESSAGE_LENGTH = 2000;
    const validatedMessage =
      summonMessage.length > MAX_SUMMON_MESSAGE_LENGTH
        ? summonMessage.slice(0, MAX_SUMMON_MESSAGE_LENGTH)
        : summonMessage;

    setPendingChatMode(validatedMode);

    // Create conversation and send the summon message
    const initSummon = async () => {
      try {
        const newConversation = await createConversation(
          validatedMessage,
          validatedMode
        );
        const newId = extractConversationId(newConversation);
        if (newId) {
          saveConversationMode(newId, validatedMode);
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
      } catch (error) {
        console.error('Failed to create summon conversation:', error);
        setFailedMessage(toFailedMessage(validatedMessage, error));
      }
    };

    initSummon();
  }, [summonMessage, summonMode]);
}
